import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

import { API_URL } from '@/config/api';

// Keep withCredentials for same-origin cookie fallback
axios.defaults.withCredentials = true;

// Axios interceptor: attach Bearer token + Accept-Language to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Forward selected UI language to backend so it can localize emails / errors
  const lang = (localStorage.getItem('i18nextLng') || 'en').split('-')[0];
  if (lang) {
    config.headers['Accept-Language'] = lang;
    config.headers['X-User-Language'] = lang;
  }
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('access_token'));

  const storeTokens = (accessToken, refreshToken) => {
    localStorage.setItem('access_token', accessToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    setToken(accessToken);
  };

  const clearTokens = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
  };

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearTokens();
      setUser(null);
    }
  }, []);

  const refreshAccessToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      const response = await axios.post(`${API_URL}/auth/refresh`, {
        refresh_token: refreshToken
      }, { withCredentials: true });
      const { access_token } = response.data;
      storeTokens(access_token, null);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  }, [logout]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
      setUser(response.data);
      return true;
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          try {
            const retryResponse = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
            setUser(retryResponse.data);
            return true;
          } catch (retryError) {
            console.error('Retry fetch user failed:', retryError);
          }
        }
      }
      return false;
    }
  }, [refreshAccessToken]);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('access_token');
      if (storedToken) {
        const success = await fetchUser();
        if (!success) {
          clearTokens();
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    };
    checkAuth();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password }, {
      withCredentials: true
    });
    const { access_token, refresh_token, user: userData } = response.data;
    storeTokens(access_token, refresh_token);
    setUser(userData);
    return { user: userData, emailVerified: response.data.email_verified };
  };

  const register = async (email, password, name, role) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      email, password, name, role
    }, { withCredentials: true });
    const { access_token, refresh_token, user: userData, message, seller_verification_required } = response.data;
    storeTokens(access_token, refresh_token);
    setUser(userData);
    return { user: userData, message, seller_verification_required };
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API_URL}/auth/forgot-password?email=${encodeURIComponent(email)}`);
    return response.data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const response = await axios.post(`${API_URL}/auth/reset-password`, {
      token: resetToken, new_password: newPassword
    });
    return response.data;
  };

  const verifyEmail = async (verificationToken) => {
    const response = await axios.post(`${API_URL}/auth/verify-email/${verificationToken}`);
    return response.data;
  };

  const resendVerification = async (email) => {
    const response = await axios.post(`${API_URL}/auth/resend-verification?email=${encodeURIComponent(email)}`);
    return response.data;
  };

  const isAuthenticated = () => !!user;
  const isAdmin = () => user?.role === 'admin';
  const isSeller = () => user?.role === 'seller';
  const isBuyer = () => user?.role === 'buyer';
  const isComplianceOfficer = () => user?.role === 'compliance_officer';
  const isSellerVerified = () => user?.seller_verified === true;

  const getAuthHeader = () => {
    const t = token || localStorage.getItem('access_token');
    if (t) return { Authorization: `Bearer ${t}` };
    return {};
  };

  const value = {
    user, token, loading,
    login, logout, register,
    forgotPassword, resetPassword, verifyEmail, resendVerification,
    isAuthenticated, isAdmin, isSeller, isBuyer, isComplianceOfficer, isSellerVerified,
    getAuthHeader, refreshAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
